/**
 * @copyright Copyright 2023-2024 Epic Systems Corporation
 * @file api collapse with links
 * @module Epic.AppOrchard.Showroom.ApiCollapse
 */

import { ChevronDownIcon, ChevronUpIcon, QuestionIcon } from "@chakra-ui/icons";
import {
	Box,
	Button,
	Center,
	Collapse,
	Flex,
	SimpleGrid,
	useBreakpointValue,
	useDisclosure,
} from "@chakra-ui/react";
import { AOLink, AOTooltip } from "ao/components/Core";
import { IGalleryApi } from "ao/types/showroom";
import React, { FunctionComponent, memo, ReactNode } from "react";

interface IProps {
	label: string;
	children: ReactNode;
	textColor?: string;
	childCount?: number;
}

export const ApiCollapse: FunctionComponent<IProps> = memo((props) => {
	const { label, children, textColor, childCount } = props;

	const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

	const columnCount = useBreakpointValue({ base: 1, sm: 2, md: 3 }) || 3;

	return (
		<Box>
			<Center width="100%">
				<Button
					colorScheme="black"
					variant="ghost"
					onClick={onToggle}
					padding="10px"
					fontSize="1.3rem"
					fontWeight="600"
					size="lg"
					color={textColor}
				>
					{label} {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
				</Button>
			</Center>
			<Collapse
				in={isOpen}
				animateOpacity
				style={{
					overflow: "visible",
				}}
			>
				<SimpleGrid columns={Math.min(columnCount, childCount ?? 3)} spacing={3} paddingTop="10px">
					{children}
				</SimpleGrid>
			</Collapse>
		</Box>
	);
});

interface IApiLinkProps {
	api: IGalleryApi;
	baseUrl: string;
	color?: string;
}

export const ApiLink: FunctionComponent<IApiLinkProps> = memo((props) => {
	const { api, baseUrl, color } = props;

	return (
		<Flex justify="center">
			<AOLink
				url={`${baseUrl}sandbox?api=${api.id}`}
				display="block"
				isExternal
				title="View the specification"
				color={color}
			>
				{api.name}
			</AOLink>
			{api.warning && (
				<>
					&nbsp;
					<AOTooltip helptext={api.warning}>
						<QuestionIcon color="red.600" boxProps={{ pb: "6px", marginLeft: "5px" }} />
					</AOTooltip>
				</>
			)}
		</Flex>
	);
});
